import React from "react";
import { imgSrc, imgSet } from "@/lib/img";
import { Headphones, Play } from "lucide-react";

function toEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

export default function MediaEmbed({ media, onPlay, className }) {
  const url = media?.url || "";
  const type = media?.media_type || "video";
  const embed = toEmbed(url);
  const isFile = url.startsWith("/api/files/");

  const handlePlay = () => {
    if (media?.id && onPlay) onPlay(media.id);
  };

  if (type === "audio" && !embed) {
    return (
      <div className={"card-dark p-5 " + (className || "")} data-testid="media-embed">
        <div className="flex items-center gap-3 mb-3">
          <Headphones className="h-5 w-5 text-copper" />
          <div>
            <p className="font-serif text-lg text-cream leading-tight">{media.title}</p>
            {media.description && <p className="text-xs text-sand mt-0.5">{media.description}</p>}
          </div>
        </div>
        <audio controls src={url} preload="none" onPlay={handlePlay} className="w-full h-10" data-testid="leader-audio-player" />
      </div>
    );
  }

  if (embed) {
    return (
      <div className={"relative bg-obsidian2 border border-white/5 " + (className || "")} data-testid="media-embed">
        <div className="relative w-full pt-[56.25%]">
          <iframe
            src={embed}
            title={media.title || "video"}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={handlePlay}
          />
        </div>
        {media.title && (
          <div className="p-4 border-t border-white/5">
            <p className="font-serif text-lg text-cream leading-tight flex items-center gap-2"><Play className="h-4 w-4 text-copper" /> {media.title}</p>
            {media.description && <p className="text-xs text-sand mt-1">{media.description}</p>}
          </div>
        )}
      </div>
    );
  }

  if (type === "video" || url.match(/\.(mp4|webm|mov)$/i)) {
    return (
      <div className={"bg-obsidian2 border border-white/5 " + (className || "")} data-testid="media-embed">
        <video controls preload="metadata" src={url} onPlay={handlePlay} className="w-full" />
        {media.title && (
          <div className="p-4 border-t border-white/5">
            <p className="font-serif text-lg text-cream leading-tight">{media.title}</p>
            {media.description && <p className="text-xs text-sand mt-1">{media.description}</p>}
          </div>
        )}
      </div>
    );
  }

  if (type === "image" && url) {
    return (
      <figure className={className} data-testid="media-embed">
        <img
          src={imgSrc(url, 1200)}
          srcSet={imgSet(url, [400, 800, 1200])}
          sizes="(max-width: 768px) 100vw, 50vw"
          alt={media.title || ""}
          className="w-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </figure>
    );
  }

  return null;
}
